/* Manages a live region for broadcasting toast messages to screen reader users

 A good practice for relying on this live region for a11y is to omit
 any interactive elements (ie links, buttons) from the toast message
 since SR users don't have a good way of navigating to the message itself

TODO(JH): I wish this were React code.
It's not because the toast is an independent react app that is re-created for
each message, so there's no place to create a live region in a component tree.
(See renderToast in toast-notification.jsx)
When this is inside app shell (FEI-3875), we should revisit this solution.
*/

const REGION_ID = "toast-live-region";

/**
 * Gets the live region element.
 *
 * If the region exists, this will clear it before it is returned.
 * If the region is not yet created, this will create it.
 */
export const getToastLiveRegion = (): HTMLElement => {
    let liveRegion = document.getElementById(REGION_ID);
    if (liveRegion) {
        clearToastLiveRegion(liveRegion);
        return liveRegion;
    }
    liveRegion = document.createElement("div");
    // "polite" waits and reads once the screen reader has a pause
    liveRegion.setAttribute("aria-live", "polite");
    // "atomic" reads the whole thing as a group (no interruptions)
    liveRegion.setAttribute("aria-atomic", "true");
    liveRegion.className = "sr-only";
    liveRegion.setAttribute("id", REGION_ID);
    liveRegion.setAttribute("data-testid", REGION_ID);
    document.body?.appendChild(liveRegion);

    return liveRegion;
};

/**
 * Clears out any existing messages in the live region.
 */
const clearToastLiveRegion = (liveRegion: HTMLElement) => {
    while (liveRegion.firstChild) {
        liveRegion.removeChild(liveRegion.firstChild);
    }
};

/**
 * Removes the live region from the DOM.
 */
export const unmountToastLiveRegion = () => {
    const liveRegion = document.getElementById(REGION_ID);
    if (liveRegion) {
        liveRegion.remove();
    }
};

/**
 * Appends a message to the live region.
 */
export const updateToastLiveRegion = (
    liveRegion: HTMLElement,
    message: HTMLElement,
) => {
    liveRegion.appendChild(message);
};
